import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  X, Search, Layers, FileText, Target, LayoutGrid,
  Loader2, Globe, Sparkles,
} from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_TABS = [
  { id: 'all', label: 'All', icon: LayoutGrid },
  { id: 'landing', label: 'Landing Pages', icon: Globe },
  { id: 'funnel', label: 'Funnels', icon: Target },
  { id: 'basic', label: 'Basic', icon: FileText },
];

const CATEGORY_STYLES = {
  landing: 'bg-blue-50 text-blue-700',
  funnel: 'bg-purple-50 text-purple-700',
  basic: 'bg-stone-100 text-stone-600',
};

function TemplateCard({ template, onSelect }) {
  const blockCount = template.blocks?.length || 0;
  const categoryLabel = template.category?.charAt(0).toUpperCase() + template.category?.slice(1);
  const isBlank = template.name === 'Blank Page';

  return (
    <div className="group bg-white rounded-xl border border-stone-200 shadow-sm hover:shadow-md hover:border-stone-300 transition-all overflow-hidden flex flex-col">
      {/* Preview area */}
      <div
        className="h-32 flex items-center justify-center relative"
        style={{
          background: isBlank
            ? '#FAFAF5'
            : 'linear-gradient(135deg, #1c1917 0%, #44403c 50%, #78716c 100%)',
        }}
      >
        {isBlank ? (
          <div className="flex flex-col items-center gap-1.5 text-stone-300">
            <FileText className="w-8 h-8" />
            <span className="text-xs font-medium">Empty Canvas</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-white/70">
            <Sparkles className="w-8 h-8" />
            <div className="flex items-center gap-1 text-xs font-medium">
              <Layers className="w-3 h-3" />
              {blockCount} blocks
            </div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h3
            className="text-sm font-semibold text-stone-900 leading-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {template.name}
          </h3>
          {template.category && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap flex-shrink-0 ${
                CATEGORY_STYLES[template.category] || CATEGORY_STYLES.basic
              }`}
            >
              {categoryLabel}
            </span>
          )}
        </div>

        <p className="text-xs text-stone-500 leading-relaxed mb-3 flex-1">
          {template.description}
        </p>

        <Button
          size="sm"
          className="w-full bg-stone-900 hover:bg-stone-800 text-white text-xs"
          onClick={() => onSelect(template)}
        >
          Use Template
        </Button>
      </div>
    </div>
  );
}

export default function TemplatePicker({ isOpen, onClose, onSelect, getAuthHeader }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/site-templates`, {
        headers: getAuthHeader(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setTemplates(data);
    } catch {
      // Silently fail — user can still pick blank
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      setSearch('');
      setActiveCategory('all');
    }
  }, [isOpen, loadTemplates]);

  if (!isOpen) return null;

  // Filter templates
  const filtered = templates.filter((t) => {
    const matchesCategory = activeCategory === 'all' || t.category === activeCategory;
    const matchesSearch =
      !search ||
      t.name?.toLowerCase().includes(search.toLowerCase()) ||
      t.description?.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Sort: Blank Page always last
  const sorted = [...filtered].sort((a, b) => {
    if (a.name === 'Blank Page') return 1;
    if (b.name === 'Blank Page') return -1;
    return (a.name || '').localeCompare(b.name || '');
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <div>
            <h2
              className="text-lg font-bold text-stone-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Choose a Template
            </h2>
            <p className="text-xs text-stone-500 mt-0.5">
              Pick a starting point for your first page
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-stone-100 transition-colors text-stone-400 hover:text-stone-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search + Category Tabs */}
        <div className="px-6 pt-4 pb-3 space-y-3 border-b border-stone-100">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
            />
          </div>

          {/* Category tabs */}
          <div className="flex gap-1.5">
            {CATEGORY_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeCategory === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveCategory(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-stone-900 text-white'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Template Grid */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-16">
              <Search className="w-8 h-8 mx-auto text-stone-200 mb-2" />
              <p className="text-sm text-stone-400">No templates found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {sorted.map((template) => (
                <TemplateCard
                  key={template.template_id}
                  template={template}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-6 py-3 border-t border-stone-100 bg-stone-50/50">
          <p className="text-[11px] text-stone-400 text-center">
            Templates provide a starting point. You can customize every block after applying.
          </p>
        </div>
      </div>
    </div>
  );
}
